import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { enrollMFA } from '../../services/auth';

interface Props {
  enrollId: string;
  qrData: { qrDataUrl: string; secret: string } | null;
  onSuccess: () => void;
}

export default function MFAEnroll({ enrollId, qrData, onSuccess }: Props) {
  const { t } = useTranslation();
  const [token, setToken] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newToken = [...token];
    newToken[index] = value.slice(-1);
    setToken(newToken);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !token[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = token.join('');
    if (otpValue.length !== 6) return;

    setError('');
    setLoading(true);

    try {
      await enrollMFA(enrollId, otpValue);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.mfa.invalid'));
      setToken(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-logo">DIMAS FARMING</div>
          <div className="auth-tagline">{t('sidebar.platform')}</div>
        </div>
      </div>

      <div className="auth-right">
        <form className="auth-form" onSubmit={handleSubmit}>
          <h1 className="auth-title">{t('auth.mfa.setup')}</h1>
          <p className="auth-subtitle">{t('auth.mfa.scanQr')}</p>

          {error && <div className="error-message">{error}</div>}

          {qrData && (
            <div className="qr-box">
              <img src={qrData.qrDataUrl} alt="QR Code" />
              <div className="secret-key">{qrData.secret}</div>
              <p className="qr-note">{t('auth.mfa.scanNote')}</p>
            </div>
          )}

          <div className="otp-wrapper">
            {token.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`otp-input ${error ? 'error' : ''}`}
              />
            ))}
          </div>

          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? t('auth.mfa.verifying') : t('auth.mfa.confirm')}
          </button>
        </form>
      </div>
    </div>
  );
}
